//@ts-check
/**
 * A log of how the app starts, for measuring it (wip-archive/app-start/APP_START.md, step 1).
 *
 * Opening the app draws Home in stages, and how long each takes inside the iOS app cannot be seen
 * from a laptop's browser. So each start notes when it reached a few points — the web app running,
 * the platform known, the session confirmed, Home on screen, the cover lifted — and, six seconds in, writes one entry
 * to the capture log with those and every API request and photo the start made, read from the
 * browser's own timings. /lab shows it. Times are ms from the web page's start: whatever iOS does
 * before that, its launch screen and starting the web view, is not seen from here.
 *
 * Lab only, and observation only, under the capture log's rules ($lib/captureprobe.js). Delete this
 * module and its five call sites once the start is settled.
 */
import { record } from "$lib/captureprobe.js";
import { isNativeShell, isProdHost } from "$lib/env.js";

/** How long after the page starts the entry is written: past Home's data and its first photos. */
const RECORD_AFTER_MS = 6000;

/** @type {Record<string, number>} */
const marks = {};
let scheduled = false;

/**
 * Note that the start reached `name`, the first time only.
 * @param {string} name
 */
export function markStart(name) {
  if (typeof window === "undefined" || isProdHost() || name in marks) return;
  marks[name] = Math.round(performance.now());
}

/** Called once, by the root layout on mounting: writes the entry when the start is over. */
export function scheduleStartRecord() {
  if (typeof window === "undefined" || isProdHost() || scheduled) return;
  scheduled = true;
  setTimeout(writeEntry, Math.max(0, RECORD_AFTER_MS - performance.now()));
}

function writeEntry() {
  try {
    const r = Math.round;
    const nav = /** @type {PerformanceNavigationTiming | undefined} */ (performance.getEntriesByType("navigation")[0]);
    const fcp = performance.getEntriesByType("paint").find((p) => p.name === "first-contentful-paint");
    const resources = /** @type {PerformanceResourceTiming[]} */ (performance.getEntriesByType("resource"));
    const requests = resources
      .filter((e) => new URL(e.name).pathname.startsWith("/api/"))
      .map((e) => ({ path: new URL(e.name).pathname, start: r(e.startTime), end: r(e.responseEnd) }))
      .sort((a, b) => a.start - b.start);
    const photos = resources.filter((e) => new URL(e.name).pathname.startsWith("/photos/"));
    record({
      kind: "start",
      native: isNativeShell(),
      navigation: nav?.type ?? "",
      firstPaint: fcp ? r(fcp.startTime) : null,
      marks: { ...marks },
      requests,
      photos: photos.length
        ? {
            count: photos.length,
            start: r(Math.min(...photos.map((e) => e.startTime))),
            end: r(Math.max(...photos.map((e) => e.responseEnd))),
          }
        : null,
    });
  } catch (err) {
    console.warn("Could not log the start", err);
  }
}
